import { UserRole, type User } from '../types';

/**
 * Espejo del catálogo de permisos por VISTA del backend
 * (server `src/common/permissions/permissions.catalog.ts`). Mantener
 * sincronizado: las claves y los defaults por rol deben coincidir. El test
 * `catalog.spec.ts` valida que los defaults reproduzcan el gating histórico.
 *
 * El permiso de vista gobierna navegación/UI (rutas + menú). La seguridad de
 * datos sigue por rol en el backend.
 */
export type ViewKey =
  | 'view:dashboard'
  | 'view:busqueda'
  | 'view:tmk-crm'
  | 'view:skytel-funnel'
  | 'view:empresas'
  | 'view:usuarios'
  | 'view:padrones'
  | 'view:padron-detalle'
  | 'view:padron-dashboard'
  | 'view:asociados'
  | 'view:reglas'
  | 'view:legajos-listos'
  | 'view:rechazados'
  | 'view:observaciones'
  | 'view:pagos-ms2'
  | 'view:adherentes'
  | 'view:cuestionarios-salud'
  | 'view:actividad'
  | 'view:alta-manual'
  | 'view:altas-directas'
  | 'view:altas-directas-masivas'
  | 'view:agregar-familiares'
  | 'view:bloqueados'
  | 'view:bajas'
  | 'view:alertas-bajas'
  | 'view:alertas-bajas-gestionar'
  | 'view:reporte-abm-web'
  | 'view:reporte-padron'
  | 'view:documentacion'
  | 'view:documentacion-subir'
  | 'view:padrones-subir'
  | 'view:padron-quitar-fila'
  | 'view:ventas-comisiones'
  | 'view:modificaciones'
  | 'view:modificaciones-sensibles'
  | 'view:modificaciones-no-sensibles'
  | 'view:recien-nacidos';

export interface PermissionEntry {
  key: ViewKey;
  /** Nombre legible para el editor de permisos. */
  label: string;
  /** Texto del item de menú; null si la vista no tiene item propio en el sidebar. */
  navLabel: string | null;
}

export const PERMISSION_CATALOG: PermissionEntry[] = [
  { key: 'view:dashboard', label: 'Dashboard', navLabel: 'Dashboard' },
  { key: 'view:busqueda', label: 'Búsqueda', navLabel: 'Búsqueda' },
  { key: 'view:tmk-crm', label: 'CRM Telemarketer', navLabel: 'CRM TMK' },
  { key: 'view:skytel-funnel', label: 'Funnel SkyTel', navLabel: 'Funnel SkyTel' },
  { key: 'view:empresas', label: 'Organizaciones', navLabel: 'Organizaciones' },
  { key: 'view:usuarios', label: 'Usuarios', navLabel: 'Usuarios' },
  { key: 'view:padrones', label: 'Padrones', navLabel: 'Padrones' },
  { key: 'view:padron-detalle', label: 'Detalle de padrón', navLabel: null },
  { key: 'view:padron-dashboard', label: 'Dashboard de padrón', navLabel: null },
  { key: 'view:asociados', label: 'Asociados (en construcción)', navLabel: null },
  { key: 'view:reglas', label: 'Reglas', navLabel: 'Reglas' },
  { key: 'view:legajos-listos', label: 'Alta Grupos', navLabel: 'Alta Grupos' },
  // Rechazados: sub-sección de Alta Grupos. Deriva de `view:legajos-listos`.
  { key: 'view:rechazados', label: 'Rechazados', navLabel: 'Rechazados' },
  { key: 'view:observaciones', label: 'Observaciones', navLabel: 'Observaciones' },
  { key: 'view:pagos-ms2', label: 'Pagos MS2', navLabel: 'Pagos MS2' },
  { key: 'view:adherentes', label: 'Alta Integrantes', navLabel: 'Alta Integrantes' },
  {
    key: 'view:cuestionarios-salud',
    label: 'Cuestionarios de salud',
    navLabel: 'Cuestionarios de salud',
  },
  { key: 'view:actividad', label: 'Actividad', navLabel: 'Actividad' },
  { key: 'view:alta-manual', label: 'Alta manual', navLabel: 'Alta manual' },
  // Wizards manuales retirados de la UI (2026-07-07): la carga por API se hace
  // desde "Alta Grupos" (/legajos-listos) y "Alta Integrantes" (/legajos-bis).
  // Las keys quedan válidas porque el backend las usa como engine; sin item de
  // menú (navLabel null) ni ruta propia.
  { key: 'view:altas-directas', label: 'Alta de grupos (retirada)', navLabel: null },
  { key: 'view:altas-directas-masivas', label: 'Alta masiva de grupos (retirada)', navLabel: null },
  { key: 'view:agregar-familiares', label: 'Agregar familiares (retirada)', navLabel: null },
  { key: 'view:bloqueados', label: 'Bloqueados', navLabel: 'Bloqueados' },
  { key: 'view:bajas', label: 'Bajas', navLabel: 'Bajas' },
  // Alertas Bajas: seguimiento (lectura). Separada de `view:bajas`, que es la
  // que ORIGINA bajas en Binary desde un archivo.
  { key: 'view:alertas-bajas', label: 'Alertas Bajas', navLabel: 'Alertas Bajas' },
  // Clave de ACCIÓN (sin item de menú): habilita resolver / archivar / reabrir
  // una alerta. Se otorga por usuario desde Usuarios.
  {
    key: 'view:alertas-bajas-gestionar',
    label: 'Alertas Bajas — permitir gestionar (resolver / archivar / reabrir)',
    navLabel: null,
  },
  // Reporte ABM Web Empresas: vista de solo lectura que muestra altas y bajas
  // de personas/legajos filtradas por estado y rango de fecha. Accesible para
  // todos los roles Medicus (ADMIN, SUPERADMIN, USER_MEDICUS).
  { key: 'view:reporte-abm-web', label: 'Reporte ABM Web Empresas', navLabel: 'Reporte ABM Web' },
  // Reporte de padrón (VEN-512): estado de cada persona cargada. Mismo gating
  // que el ABM: Medicus por default, la organización con un override `allow`.
  { key: 'view:reporte-padron', label: 'Reporte de padrón', navLabel: 'Reporte de padrón' },
  { key: 'view:documentacion', label: 'Documentación', navLabel: 'Documentación' },
  // Clave de ACCIÓN (sin item de menú): habilita subir archivos. Aparece en el
  // editor de permisos para otorgarla por usuario (ej. a un USER_MEDICUS).
  {
    key: 'view:documentacion-subir',
    label: 'Documentación — permitir subir archivos',
    navLabel: null,
  },
  // Clave de ACCIÓN sin menú (espejo del server). La tienen por rol la
  // organización (para las suyas) y los admins Medicus (para todas); Binary
  // solo SUMA (un USER_MEDICUS promotor vigente del empleador). `deny` se la
  // saca a cualquiera; `allow` habilita a quien no figura.
  {
    key: 'view:padrones-subir',
    label: 'Padrones — permitir subir',
    navLabel: null,
  },
  // Clave de ACCIÓN sin menú (espejo del server): quitar una fila de un padrón
  // ya cerrado (pendiente de validación), que además la desvincula del legajo.
  // Default solo SUPERADMIN; se otorga por usuario desde Usuarios (VEN-636).
  {
    key: 'view:padron-quitar-fila',
    label: 'Padrones — permitir quitar filas de un padrón cerrado (pendiente de validación)',
    navLabel: null,
  },
  { key: 'view:ventas-comisiones', label: 'Ventas / Comisiones', navLabel: 'Ventas / Comisiones' },
  // Modificación de datos (F2-6). UN item de menú con dos tabs adentro (el
  // sidebar es plano), y dos claves de ACCIÓN sin item propio que habilitan
  // CARGAR cada flujo. Ver la sección la tiene también Medicus, para seguir los
  // trámites y consultar el log (RF-015 / CA-012); cargar no, porque RF-013
  // pide que Medicus no sea rol intermediario.
  {
    key: 'view:modificaciones',
    label: 'Modificación de datos',
    navLabel: 'Modificación de datos',
  },
  {
    key: 'view:modificaciones-sensibles',
    label: 'Modificación de datos — permitir cargar Datos Sensibles',
    navLabel: null,
  },
  {
    key: 'view:modificaciones-no-sensibles',
    label: 'Modificación de datos — permitir cargar Datos No Sensibles',
    navLabel: null,
  },
  // Recién nacidos (VEN-513): Binary los tiene con datos básicos, el portal los
  // suma al legajo y la organización los completa. Espejo del backend.
  { key: 'view:recien-nacidos', label: 'Recién nacidos', navLabel: 'Recién nacidos' },
];

export const ALL_VIEW_KEYS: ViewKey[] = PERMISSION_CATALOG.map((e) => e.key);

/**
 * Sub-secciones que ACOMPAÑAN a su sección madre. Espejo de VISTAS_DERIVADAS
 * del backend — si divergen, el menú muestra una cosa y el backend responde
 * otra. `/rechazados` y `/alertas-bajas` tenían item de menú pero no clave
 * propia: viajaban con la de otra sección. Al dárselas, la herencia evita mover
 * a nadie de lugar (un `deny` sobre la madre arrastra a la hija) sin migrar
 * datos. Un allow/deny explícito sobre la hija siempre gana.
 */
export const VISTAS_DERIVADAS: Partial<Record<ViewKey, ViewKey>> = {
  'view:rechazados': 'view:legajos-listos',
  'view:alertas-bajas': 'view:bajas',
};

/** Defaults por rol — reproducen el gating histórico de rutas (no-op). */
/**
 * Vistas que existen en el código pero NO se entregan por default a NINGÚN rol,
 * ni al SUPERADMIN. Solo se obtienen con un `allow` explícito por usuario.
 *
 * **Espejo de `VISTAS_OCULTAS` del backend** — si divergen, falla el test del
 * catálogo. El que manda es el backend (`user.permisos` ya viene resuelto); esto
 * cubre el camino de fallback de `vistasDelUsuario` y mantiene una sola verdad.
 *
 * **Hoy la lista está VACÍA: no hay ninguna sección oculta.** El mecanismo queda
 * en pie para volver a desplegar una pantalla apagada y abrirla sin redeploy.
 *
 * Historia: Modificación de datos se desplegó oculta el 03/09/2026 y se habilitó
 * el 04/09/2026 sacando sus tres claves de acá y del backend. Los defaults por
 * rol nunca se tocaron, así que volvió el gating que ya estaba pensado.
 */
export const VISTAS_OCULTAS: readonly ViewKey[] = [];

const OCULTAS = new Set<string>(VISTAS_OCULTAS);

export const ROLE_DEFAULT_VIEWS: Record<UserRole, ViewKey[]> = {
  [UserRole.SUPERADMIN_MEDICUS]: [...ALL_VIEW_KEYS],
  // `view:alta-manual` y `view:skytel-funnel` NO van por default al ADMIN:
  // alta-manual y la supervisión TMK (god-mode del CRM) quedan solo para el
  // SUPERADMIN por default; se habilitan a otros usuario por usuario con `allow`.
  [UserRole.ADMIN_MEDICUS]: ALL_VIEW_KEYS.filter(
    (k) =>
      k !== 'view:alta-manual' &&
      k !== 'view:altas-directas' &&
      k !== 'view:altas-directas-masivas' &&
      k !== 'view:agregar-familiares' &&
      // Alta Integrantes (bis): solo SUPERADMIN por default; se habilita por
      // usuario con un override `allow` desde Usuarios.
      k !== 'view:adherentes' &&
      k !== 'view:skytel-funnel' &&
      k !== 'view:bloqueados' &&
      k !== 'view:bajas' &&
      // Rechazados y Alertas Bajas NO van en la semilla: se heredan de su
      // sección madre. Sembrarlas rompería el arrastre del `deny`.
      k !== 'view:rechazados' &&
      k !== 'view:alertas-bajas' &&
      k !== 'view:alertas-bajas-gestionar' &&
      k !== 'view:padron-quitar-fila' &&
      k !== 'view:ventas-comisiones' &&
      // Medicus VE Modificación de datos (le cae por la lista negra) pero no
      // CARGA archivos: RF-013 pide que no sea rol intermediario del proceso.
      k !== 'view:modificaciones-sensibles' &&
      k !== 'view:modificaciones-no-sensibles',
  ),
  // Observaciones SÍ la ve (en modo lectura: las acciones siguen gateadas por rol).
  // Documentación: la ve y descarga, pero NO sube por default (view:documentacion-subir
  // se otorga por override desde la pantalla de Usuarios).
  [UserRole.USER_MEDICUS]: ALL_VIEW_KEYS.filter(
    (k) =>
      k !== 'view:usuarios' &&
      k !== 'view:tmk-crm' &&
      k !== 'view:skytel-funnel' &&
      k !== 'view:alta-manual' &&
      k !== 'view:altas-directas' &&
      k !== 'view:altas-directas-masivas' &&
      k !== 'view:agregar-familiares' &&
      k !== 'view:adherentes' &&
      k !== 'view:bloqueados' &&
      k !== 'view:bajas' &&
      k !== 'view:rechazados' &&
      k !== 'view:alertas-bajas' &&
      k !== 'view:alertas-bajas-gestionar' &&
      k !== 'view:documentacion-subir' &&
      k !== 'view:padrones-subir' &&
      k !== 'view:padron-quitar-fila' &&
      k !== 'view:ventas-comisiones' &&
      // Idem ADMIN: ve la sección, no carga.
      k !== 'view:modificaciones-sensibles' &&
      k !== 'view:modificaciones-no-sensibles',
  ),
  [UserRole.USUARIO_EMPRESA]: [
    'view:dashboard',
    'view:busqueda',
    'view:padrones',
    // Subir padrones de SUS organizaciones va por rol (espejo del server): no
    // depende de figurar en Binary ni de que Oracle responda.
    'view:padrones-subir',
    'view:padron-detalle',
    'view:asociados',
    'view:reglas',
    'view:observaciones',
    'view:actividad',
    // Alta Integrantes: la organización ve en qué estado están sus grupos y
    // las últimas altas, pero NO despacha a Binary. El corte no es cosmético:
    // los endpoints de envío siguen siendo ADMIN/SUPERADMIN en el backend.
    'view:adherentes',
    // Alertas Bajas: la organización ve el ESTADO de los casos de baja de su
    // gente (scopeado por empresa en el backend), pero no los gestiona —
    // `view:alertas-bajas-gestionar` no está acá y el POST le da 403.
    'view:alertas-bajas',
    'view:documentacion',
    'view:documentacion-subir',
    // Modificación de datos: la organización es quien CARGA los archivos, así
    // que tiene las tres (ver + los dos flujos).
    'view:modificaciones',
    'view:modificaciones-sensibles',
    'view:modificaciones-no-sensibles',
    'view:recien-nacidos',
  ],
  // TELEMARKETER: Búsqueda (lookup general) + su CRM de contactos.
  [UserRole.TELEMARKETER]: ['view:busqueda', 'view:tmk-crm'],
  // TELEMARKETER_MEDICUS (interno): MISMAS vistas que el externo. Espejo de
  // ROLE_DEFAULT_VIEWS del backend — si divergen, falla el test del catálogo.
  [UserRole.TELEMARKETER_MEDICUS]: ['view:busqueda', 'view:tmk-crm'],
  // SKYTEL (agente externo): solo el CRM TMK (scopeado a la cohorte en el backend).
  [UserRole.SKYTEL]: ['view:tmk-crm'],
  // INTEGRACION: ninguna vista. Espejo del backend — si divergen, falla el test
  // del catalogo. Es un sistema que consume la API, no entra a la web.
  [UserRole.INTEGRACION]: [],
};

export interface PermisoOverrides {
  allow: string[];
  deny: string[];
}

/**
 * Permiso efectivo = defaults del rol ⊕ overrides. El SUPERADMIN siempre ve
 * todo (inmune a deny); deny gana sobre allow; ignora claves inválidas.
 * Misma lógica que el backend.
 */
export function permisosEfectivos(rol: UserRole, overrides?: PermisoOverrides): ViewKey[] {
  const valido = new Set<string>(ALL_VIEW_KEYS);
  const allow = new Set((overrides?.allow ?? []).filter((k) => valido.has(k)));
  const deny = new Set((overrides?.deny ?? []).filter((k) => valido.has(k)));
  // El superadmin sigue inmune al `deny`, pero no se saltea las ocultas.
  if (rol === UserRole.SUPERADMIN_MEDICUS) {
    return ALL_VIEW_KEYS.filter((k) => !OCULTAS.has(k) || allow.has(k));
  }
  const base = new Set<string>(ROLE_DEFAULT_VIEWS[rol] ?? []);
  // Antes de los overrides: ocultar es sacarla del DEFAULT, no prohibirla.
  for (const k of OCULTAS) base.delete(k);
  for (const k of allow) base.add(k);
  for (const k of deny) base.delete(k);
  // Herencia: la hija sin decisión propia acompaña a la madre ya resuelta.
  for (const [hija, madre] of Object.entries(VISTAS_DERIVADAS)) {
    if (allow.has(hija) || deny.has(hija)) continue;
    if (madre && base.has(madre)) base.add(hija);
  }
  return ALL_VIEW_KEYS.filter((k) => base.has(k));
}

/**
 * Vistas que puede ver el usuario. Usa `user.permisos` si el backend lo mandó
 * (ya trae overrides + inmunidad SA aplicados); si no (backend viejo), cae a
 * los defaults del rol → comportamiento idéntico al histórico.
 */
export function vistasDelUsuario(user: Pick<User, 'rol' | 'permisos' | 'overrides'>): Set<ViewKey> {
  if (user.permisos && user.permisos.length) return new Set(user.permisos);
  return new Set(permisosEfectivos(user.rol, user.overrides));
}

export function puedeVer(
  user: Pick<User, 'rol' | 'permisos' | 'overrides'>,
  key: ViewKey,
): boolean {
  return vistasDelUsuario(user).has(key);
}

/** Ruta de aterrizaje: la primera vista navegable que el usuario puede ver. */
export function rutaInicial(user: Pick<User, 'rol' | 'permisos' | 'overrides'>): string {
  const vistas = vistasDelUsuario(user);
  if (vistas.has('view:dashboard')) return '/dashboard';
  if (vistas.has('view:busqueda')) return '/busqueda';
  const primera = PERMISSION_CATALOG.find((e) => e.navLabel && vistas.has(e.key));
  return primera ? RUTA_POR_VISTA[primera.key] : '/login';
}

/** Mapa vista → ruta del front (para fallbacks de navegación). */
export const RUTA_POR_VISTA: Record<ViewKey, string> = {
  'view:dashboard': '/dashboard',
  'view:busqueda': '/busqueda',
  'view:tmk-crm': '/tmk-crm',
  'view:skytel-funnel': '/skytel-funnel',
  'view:empresas': '/empresas',
  'view:usuarios': '/usuarios',
  'view:padrones': '/padrones',
  'view:padron-detalle': '/padrones',
  'view:padron-dashboard': '/padrones',
  'view:asociados': '/asociados',
  'view:reglas': '/reglas',
  'view:legajos-listos': '/legajos-listos',
  'view:rechazados': '/rechazados',
  'view:observaciones': '/observaciones',
  'view:pagos-ms2': '/pagos-ms2',
  'view:adherentes': '/legajos-bis',
  'view:cuestionarios-salud': '/cuestionarios-salud',
  'view:actividad': '/actividad',
  'view:alta-manual': '/alta-manual',
  'view:altas-directas': '/altas-directas',
  'view:altas-directas-masivas': '/altas-directas-masivas',
  'view:agregar-familiares': '/agregar-familiares',
  'view:bloqueados': '/bloqueados',
  'view:bajas': '/bajas',
  'view:alertas-bajas': '/alertas-bajas',
  'view:alertas-bajas-gestionar': '/alertas-bajas',
  'view:reporte-abm-web': '/reporte-abm-web',
  'view:reporte-padron': '/reporte-padron',
  'view:documentacion': '/documentacion',
  'view:documentacion-subir': '/documentacion',
  'view:padrones-subir': '/padrones',
  'view:padron-quitar-fila': '/padrones/:id',
  'view:ventas-comisiones': '/ventas-comisiones',
  'view:modificaciones': '/modificaciones',
  'view:modificaciones-sensibles': '/modificaciones',
  'view:modificaciones-no-sensibles': '/modificaciones',
  'view:recien-nacidos': '/recien-nacidos',
};
